import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  CloudAccountChangeV2,
  providerToAuthCloud,
  useCurrentCloudProvider,
  useCloudAccounts,
} from '@labrav/common'
import {
  CustomLoading,
  ModalConfirmV2,
  ModalSizeVariants,
  useAuth,
} from '@labrav/react-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomProductSelectorDropdown } from '../CustomProductSelectorDropdown/CustomProductSelectorDropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import {
  FlyoutFunction,
  ProductStatus,
} from '../../../modules/productsListing/types'
import { isEmpty } from 'lodash'
import { useStyles } from './ModalToCreateResaleAuthorization.styles'
import { useParams } from 'react-router-dom'
import { getProducts } from '../../../modules/productsListing/action'
import { useDispatch } from '../../../store'
import { Box } from '@mui/material'
import { useFlagValue } from '@labrav/flags'
import type { ResaleAuthorizationModalProps } from './types'

export type { ResaleAuthorizationModalProps } from './types'

export const ModalToCreateResaleAuthorization: React.FC<
  ResaleAuthorizationModalProps
> = ({
  open = false,
  hideCancelIcon = false,
  onClose,
  handleCreateResaleAuthorization,
  handleProductChange,
  selectedAccountIdWithCloud,
  handleCloudAccountChange,
  externalBaseURL,
  isCloudDisabled = false,
  accountId,
}) => {
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [productsLoaded, setProductsLoaded] = useState<boolean>(false)
  const { classes } = useStyles()
  const { canDo, loading: authLoading } = useAuth()
  const { value: isAzureResaleAuthorizationMappingManagerEnabled } =
    useFlagValue('azureMarketplaceResaleAuthorizationMappingManager')

  const handleProductChangeLocal = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)
      handleProductChange ? handleProductChange(productId) : null
    },
    [handleProductChange]
  )
  const { partnerId } = useParams<{ partnerId: string }>()
  const { products } = useFlyOutProductsBasedOnSelectedCloud({
    microsoftResellerAuthorization: true,
  })
  const selectedProductStatus = products.find(
    product => product.productId === selectedProductId
  )?.status
  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')
  const marketplaceCloudAccounts = useMemo(() => {
    if (authLoading) return []

    return baseMarketplaceCloudAccounts.map(account => {
      const hasPermission = canDo({
        cloud: providerToAuthCloud(account.provider),
        service: 'marketplace',
        action: 'create',
      })
      const accountProvider = account.provider.toUpperCase()
      const isAzure = accountProvider === 'AZURE'
      const isGcp = accountProvider === 'GCP'

      return {
        ...account,
        disabled:
          isGcp ||
          (isAzure
            ? !isAzureResaleAuthorizationMappingManagerEnabled
            : !hasPermission),
      }
    })
  }, [
    authLoading,
    baseMarketplaceCloudAccounts,
    canDo,
    isAzureResaleAuthorizationMappingManagerEnabled,
  ])
  const cloudMarketplace = useCurrentCloudProvider({
    microsoftResellerAuthorization: true,
  })
  const dispatch = useDispatch()

  useEffect(() => {
    if (!isEmpty(products)) {
      setProductsLoaded(true)
      return
    }

    if (isEmpty(partnerId) || !cloudMarketplace || productsLoaded) {
      return
    }

    const currentPartnerId = accountId || (partnerId as string)
    const fetchProducts = async () => {
      await dispatch(getProducts(currentPartnerId, cloudMarketplace))
      setProductsLoaded(true)
    }

    if (externalBaseURL && open) {
      fetchProducts()
    } else if (!externalBaseURL) {
      fetchProducts()
    }
  }, [
    dispatch,
    partnerId,
    cloudMarketplace,
    products,
    productsLoaded,
    open,
    externalBaseURL,
    accountId,
  ])

  return (
    <ModalConfirmV2
      title={'Select a product'}
      description={
        "Select a product for which you'd like to create a reseller authorization."
      }
      content={
        <>
          {productsLoaded ? (
            <div className={classes.cloudSelectorWrapper}>
              {handleCloudAccountChange && (
                <div className={classes.cloudSelectorWidthHeight}>
                  <CloudAccountChangeV2
                    cloudAccounts={marketplaceCloudAccounts}
                    selectedAccountId={selectedAccountIdWithCloud || ''}
                    onChange={handleCloudAccountChange}
                    width={220}
                    size="small"
                    disabled={isCloudDisabled}
                  />
                </div>
              )}
              <CustomProductSelectorDropdown
                functionType={FlyoutFunction.RESALE_AUTHORIZATIONS}
                onProductChange={handleProductChangeLocal}
                openNewPrivateOfferModal={open}
              />
            </div>
          ) : (
            <Box className={classes.box}>
              <CustomLoading
                loadingTitle="Products are being fetched"
                loadingSubTitle="It may take a few moments, please wait..."
              />
            </Box>
          )}
        </>
      }
      open={open}
      titleAcceptButton={'Create reseller authorization'}
      onAccept={
        handleCreateResaleAuthorization
          ? handleCreateResaleAuthorization
          : () => {}
      }
      onDecline={onClose ? onClose : () => {}}
      variant={ModalSizeVariants.medium}
      acceptDisabled={
        isEmpty(selectedProductId) ||
        selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED
      }
      disabledAcceptButtonMessage={
        selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED
          ? 'Authorization creation is not allowed by AWS for this restricted product.'
          : 'Select one product to create a reseller authorization.'
      }
      icon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '12px', height: '12px' }}
        />
      }
      hideCancelIcon={hideCancelIcon}
      hideCancelButton={true}
    />
  )
}
